import { useState } from 'react';
import ErrorBoundary from '../common/ErrorBoundary';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboardIcon,
  FileTextIcon,
  CoinsIcon,
  FolderOpenIcon,
  MessageSquareIcon,
  BellIcon,
  LogOutIcon,
  MenuIcon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { ROUTES } from '../../constants/routes';
import { cn } from '../../utils/cn';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';

const BROKER_NAV = [
  { section: 'Overview', items: [
    { label: 'Dashboard', href: ROUTES.BROKER_DASHBOARD, icon: LayoutDashboardIcon, exact: true },
  ]},
  { section: 'Portfolio', items: [
    { label: 'Policies', href: ROUTES.BROKER_POLICIES, icon: FileTextIcon },
    { label: 'Commissions', href: ROUTES.BROKER_COMMISSIONS, icon: CoinsIcon },
    { label: 'Documents', href: ROUTES.BROKER_DOCUMENTS, icon: FolderOpenIcon },
  ]},
  { section: 'Communication', items: [
    { label: 'Messages', href: ROUTES.BROKER_MESSAGES, icon: MessageSquareIcon },
  ]},
];

function BrokerSidebar({ onClose }: { onClose?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const user = useAuthStore((s) => s.user);
  const branding = useBrandingStore((s) => s.config);

  const logoUrl = branding?.logo_url;
  const companyName = useAuthStore((s) => s.company)?.name || 'Portal';

  function handleNav(href: string) {
    onClose?.();
    navigate(href);
  }

  function handleSignOut() {
    onClose?.();
    clearAuth();
    queryClient.clear();
    navigate(ROUTES.LOGIN, { replace: true });
  }

  return (
    <div className="flex h-full flex-col bg-white border-r border-gray-200">
      {/* Brand */}
      <div className="flex h-16 items-center gap-3 px-4 border-b border-gray-100">
        {logoUrl ? (
          <img src={logoUrl} alt={companyName} className="h-8 w-auto object-contain" />
        ) : (
          <span className="text-base font-bold text-gray-900 truncate">{companyName}</span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
        {BROKER_NAV.map((section) => (
          <div key={section.section}>
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {section.section}
            </p>
            {section.items.map((item) => {
              const isActive = item.exact
                ? pathname === item.href
                : pathname === item.href || pathname.startsWith(item.href + '/');
              return (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => handleNav(item.href)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors mb-1',
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-100 p-4 space-y-2">
        {user && (
          <p className="text-xs text-gray-400 truncate">{user.email}</p>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-gray-600 hover:text-gray-900 px-2"
          onClick={handleSignOut}
        >
          <LogOutIcon className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}

export default function BrokerLayout() {
  const isIcBroker = useAuthStore((s) => s.isIcBroker);
  const unreadCount = useNotificationStore((s) => s.unread_count);
  const setDrawerOpen = useNotificationStore((s) => s.setDrawerOpen);
  const [mobileOpen, setMobileOpen] = useState(false);
  const branding = useBrandingStore((s) => s.config);
  const companyName = useAuthStore((s) => s.company)?.name || 'Portal';

  if (!isIcBroker()) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 flex-col flex-shrink-0">
        <BrokerSidebar />
      </aside>

      {/* Mobile nav */}
      <Sheet open={mobileOpen} onOpenChange={(o) => !o && setMobileOpen(false)}>
        <SheetContent side="left" className="p-0 w-60">
          <SheetHeader className="sr-only">
            <SheetTitle>Broker Portal Navigation</SheetTitle>
          </SheetHeader>
          <BrokerSidebar onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* TopNav */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon className="h-5 w-5" />
            </Button>
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt={companyName} className="h-7 w-auto object-contain" />
            ) : (
              <span className="text-sm font-bold text-gray-900">{companyName}</span>
            )}
            <span className="text-xs text-gray-400">Broker Portal</span>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setDrawerOpen(true)}
            aria-label="Notifications"
          >
            <BellIcon className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full p-0 text-[10px] bg-red-500 text-white border-0">
                {unreadCount}
              </Badge>
            )}
          </Button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
